import { TabStyles } from '@tableau/widgets';
import { DataGrid, HeaderColumn } from '@tableau/widgets-datagrid';
import React from 'react';
import { findDOMNode } from 'react-dom';
import { SvgNarrowSortArrowAscending, SvgNarrowSortArrowDescending } from '../../../../../../gen-src/icons';
import { OrderDirection } from '../../../../../../gen-src/queries';
import { divWithStyle } from '../../style/LayoutComponents';
import { elideTextStyle } from '../../style/LayoutStyles';
import { Style } from '../../style/StyleFunctions';
import { vizPortalFontStyle } from '../../style/VizPortalStyles';
import { GridSortProps } from '../sort/GridSortProps';
import { OrderBy } from '../sort/OrderBy';

// styles from vizportalclient, src/app/spaces/Explorer/ExplorerStyles.ts
export const textHeaderStyle: Style = {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  fontSize: TabStyles.Typography.Sizing.Default,
  fontWeight: 'bold',
  color: TabStyles.Typography.Color.PrimaryLight,
  paddingLeft: TabStyles.Spacing.MediumSpace,
  paddingRight: TabStyles.Spacing.MediumSpace,
};


// A version of the DataGrid.Column interface that is type-safe in only the ways we care about
// for the header formatter.
type ColumnForHeaderFormatter<OrderFields extends string> =
  Pick<DataGrid.Column, 'headerText' | 'name' | 'sortable'> &
  { dataKey: OrderFields };

// Shamelessly copied from datagrid code
export function measureWidth(componentOrElement: React.Component<unknown, unknown, unknown> | Element): number {
  // eslint-disable-next-line react/no-find-dom-node
  const domNode: Element = findDOMNode(componentOrElement) as Element;
  const rect = domNode.getBoundingClientRect();
  return Math.ceil(rect.right - rect.left);
}

type SortableHeaderFormatterColumnProps<OrderFields extends string> = {
  readonly col: ColumnForHeaderFormatter<OrderFields>; // stricter type on `col` than from the normal datagrid props
  readonly currentSort?: OrderBy<OrderFields>;
  readonly changeSort?: (field: OrderFields) => void;
  readonly textAlign?: 'right';
};

const SortableHeaderCellContainer = divWithStyle('SortableHeaderCellContainer', {
  ...elideTextStyle,
  ...vizPortalFontStyle,
  ...textHeaderStyle,
  paddingLeft: 0, // Sort arrow sits in space usually occupied by padding
  boxSizing: 'border-box',
});

const SortableHeaderCellText = divWithStyle('SortableHeaderCellText', {
  ...elideTextStyle,
  flex: '1 1 auto',
});

/**
 * A header formatter that displays the column text and also an arrow showing the currently-applied sort order
 */
export class SortableHeaderFormatter<OrderFields extends string>
  extends React.Component<HeaderColumn.HeaderFormatterProps & SortableHeaderFormatterColumnProps<OrderFields>> {

  private hasReportedWidth = false;

  public override render() {
    const col = this.props.col;
    const isSortable = col.sortable === true;

    const extraContainerStyles: React.CSSProperties = {
      textAlign: this.props.textAlign ? this.props.textAlign : undefined,
      textDecoration: isSortable && this.props.hovered ? 'underline' : 'none',
      color: this.isSorted ? TabStyles.Typography.Color.PrimaryDark : TabStyles.Typography.Color.PrimaryLight,
    };

    // VizPortal has support for sort orders that are "directionless" (e.g. relevance, which can be only descending).
    // We may eventually support that too, but currently all our sorts have two directions.
    const supportsDirections = true;

    return (
      <SortableHeaderCellContainer
        style={extraContainerStyles}
        onClick={this.handleClickOrTap}
        onTouchStart={this.handleClickOrTap}
        data-tb-test-id={`display-grid-header-${this.props.col.dataKey}`}
      >
        <ArrowIcon sorted={this.isSorted} ascending={this.ascending} supportsAscDesc={supportsDirections} />
        <SortableHeaderCellText>
          {col.headerText}
        </SortableHeaderCellText>
      </SortableHeaderCellContainer>
    );
  }

  public override componentDidMount() {
    // getBoundingClientRect doesnt play nice with overflow: 'hidden' property.
    // so measure width of header and add a 6 pixel padding
    // to ensure we don't elide header column text on initial render
    if (!this.hasReportedWidth && this.props.reportWidthFn) {
      this.hasReportedWidth = true;
      this.props.reportWidthFn(this.props.col.name, measureWidth(this) + 6);
    }
  }

  private get isSorted(): boolean {
    return this.props.currentSort ? (this.props.currentSort.field.toLowerCase() === this.props.col.dataKey.toLowerCase()) : false;
  }

  private get ascending(): boolean {
    return this.props.currentSort ? (this.props.currentSort.direction.toLowerCase() === OrderDirection.ASC.toLowerCase()) : false;
  }

  private handleClickOrTap = () => {
    if (this.props.col.sortable && this.props.changeSort) {
      this.props.changeSort(this.props.col.dataKey);
    }
  }
}

type ArrowIconProps = {
  readonly sorted: boolean;
  readonly ascending: boolean;
  readonly supportsAscDesc: boolean;
};

// arrow takes up left padding of hcell
const arrowWidth = TabStyles.Spacing.MediumSpace;
const arrowHeight = 19;

export function ArrowIcon(props: ArrowIconProps) {
  const arrowImg = getArrowImg(props);

  const arrowStyles = {
    width: arrowWidth,
    height: arrowHeight,
    display: 'inline-block',
    verticalAlign: 'middle',
  };
  return <div style={arrowStyles}>{arrowImg}</div>;
}

function getArrowImg(props: ArrowIconProps): JSX.Element | null {
  if (props.sorted && props.supportsAscDesc) {
    if (props.ascending) {
      return <SvgNarrowSortArrowAscending height={arrowHeight} />;
    } else {
      return <SvgNarrowSortArrowDescending height={arrowHeight} />;
    }
  } else {
    return null;
  }
}

/**
 * Creates a "text header formatter" for grids whose columns could be sortable.
 * The sortable functionality will be enabled only if the column has `sortable: true`,
 * otherwise it will behave like a normal unsortable text header formatter.
 *
 * So, this "sortable" header formatter CAN be used for unsortable columns (it is preferred to do so,
 * because then sortability can be toggled simply by changing a boolean).
 */
export function createSortableHeaderFormatter<OrderFields extends string>(
  gridSortProps: Partial<GridSortProps<OrderFields>>,
  textAlign?: 'right'
): React.Factory<HeaderColumn.HeaderFormatterProps> {
  // eslint-disable-next-line react/display-name
  return (props: HeaderColumn.HeaderFormatterProps) => (
    <SortableHeaderFormatter
      {...props}
      currentSort={gridSortProps.orderBy}
      changeSort={gridSortProps.updateSortOrder}
      textAlign={textAlign}
    />
  );
}
